import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ArabicKeyboard from './ArabicKeyboard';
import { useCaptions } from '../context/CaptionContext';
import { useI18n, rich } from '../../i18n';
import './InlineWordEditor.css';

const InlineWordEditor = ({ 
  word, 
  anchorRect, 
  isNonArabic = false,
  onSave, 
  onClose, 
  onOpenTiming, 
  phrase,
  onSelectAlternative,
  displayMode,
  fontFamily
}) => {
  const { captionData, updateCaptionData } = useCaptions();
  const { t } = useI18n();
  const [text, setText] = useState(word.text || '');
  const [showKeyboard, setShowKeyboard] = useState(true);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const containerRef = useRef(null);
  const isArabizi = displayMode === 'arabizi';
  
  // Replace all occurrences feature
  const [originalTextBeforeEdit] = useState(() => {
    return isArabizi ? (word.arabizi || word.text || '') : (word.text || '');
  });
  const [showReplaceAllModal, setShowReplaceAllModal] = useState(false);
  const [replaceAllInfo, setReplaceAllInfo] = useState({ count: 0, newText: '', matchingWordIds: [] });

  // Compute phrase display info
  const phraseInfo = useMemo(() => {
    if (!phrase) return null;
    
    const hasAlternatives = phrase.alternatives && phrase.alternatives.length > 0;
    const isOriginalActive = phrase.activeAlternativeId === null;
    
    const originalText = phrase.originalWords
      .map(w => isArabizi ? (w.arabizi || w.text) : w.text)
      .join(' ');
    
    return {
      hasAlternatives,
      isOriginalActive,
      originalText,
      alternatives: phrase.alternatives || [],
      label: phrase.label || 'Phrase'
    };
  }, [phrase, isArabizi]);

  // Position the editor near the word
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [maxHeight, setMaxHeight] = useState(null);

  useEffect(() => {
    if (anchorRect) {
      const padding = 8;
      const topBarOffset = 72; // keep below the top bar
      const editorWidth = 340;
      
      let left = anchorRect.left + anchorRect.width / 2 - editorWidth / 2;
      let top = Math.max(anchorRect.bottom + padding, topBarOffset + padding);

      // Keep within viewport horizontally
      if (left < padding) left = padding;
      if (left + editorWidth > window.innerWidth - padding) {
        left = window.innerWidth - editorWidth - padding;
      }

      // Always keep below the word (do not flip above)
      const spaceBelow = window.innerHeight - top - padding;
      setMaxHeight(Math.max(spaceBelow, 120));
      setPosition({ top, left });
    }
  }, [anchorRect, showKeyboard, showAlternatives, phraseInfo]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        handleSave();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [text]);

  const handleSave = () => {
    const trimmedText = text.trim();
    
    // Check if text actually changed
    const currentText = isArabizi ? (word.arabizi || word.text || '') : (word.text || '');
    if (trimmedText === currentText) {
      onClose();
      return;
    }
    
    // Find other words with the same original text
    const matchingWords = captionData.words.filter(w => {
      if (w.id === word.id) return false; // Exclude current word
      if (w.type === 'punctuation' || w.type === 'silence') return false; // Exclude non-words
      
      const wordText = isArabizi ? (w.arabizi || w.text || '') : (w.text || '');
      return wordText === originalTextBeforeEdit;
    });
    
    // If there are other matching words, show the replace all modal
    if (matchingWords.length > 0) {
      setReplaceAllInfo({
        count: matchingWords.length + 1, // +1 for current word
        newText: trimmedText,
        matchingWordIds: matchingWords.map(w => w.id)
      });
      setShowReplaceAllModal(true);
      setShowKeyboard(false);
    } else {
      // No duplicates, save normally
      onSave(trimmedText);
    }
  };

  // Replace all matching words with new text
  const handleReplaceAll = () => {
    const { newText, matchingWordIds } = replaceAllInfo;
    
    // Update all matching words including current word
    const allWordIdsToUpdate = [word.id, ...matchingWordIds];
    const newWords = captionData.words.map(w => {
      if (allWordIdsToUpdate.includes(w.id)) {
        if (isArabizi) {
          return { ...w, arabizi: newText };
        } else {
          return { ...w, text: newText };
        }
      }
      return w;
    });
    
    updateCaptionData({ ...captionData, words: newWords });
    setShowReplaceAllModal(false);
    onClose();
  };

  // Replace only the current word
  const handleReplaceOnlyThis = () => {
    const { newText } = replaceAllInfo;
    onSave(newText);
    setShowReplaceAllModal(false);
  };

  // Cancel the replace all modal - close the entire editor without saving
  const handleCancelReplaceAll = () => {
    setShowReplaceAllModal(false);
    setReplaceAllInfo({ count: 0, newText: '', matchingWordIds: [] });
    onClose(); // Close the entire editor
  };

  const handleOpenTiming = () => {
    // Save any text changes first
    if (text.trim() !== word.text) {
      onSave(text.trim());
    }
    onOpenTiming();
  };

  const handleSelectAlternative = (alternativeId) => {
    if (onSelectAlternative) {
      onSelectAlternative(phrase.id, alternativeId);
      onClose();
    }
  };

  return createPortal(
    <div 
      ref={containerRef}
      className="inline-word-editor"
      style={{ top: position.top, left: position.left, maxHeight: maxHeight ? `${maxHeight}px` : undefined, overflowY: 'auto' }}
    >
      {/* Action bar */}
      <div className="editor-action-bar">
        <button 
          className={`action-toggle ${showKeyboard ? 'active' : ''} tooltip-trigger`}
          onClick={() => {
            setShowKeyboard(!showKeyboard);
            if (!showKeyboard) setShowAlternatives(false);
          }}
          data-tooltip={t('Toggle Arabic Keyboard')}
          aria-label={t('Toggle Arabic Keyboard')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <line x1="6" y1="8" x2="6" y2="8.01" />
            <line x1="10" y1="8" x2="10" y2="8.01" />
            <line x1="14" y1="8" x2="14" y2="8.01" />
            <line x1="18" y1="8" x2="18" y2="8.01" />
            <line x1="8" y1="16" x2="16" y2="16" />
          </svg>
          <span>{t('Keyboard')}</span>
        </button>

        {phraseInfo?.hasAlternatives && !isNonArabic && (
          <button 
            className={`action-toggle ${showAlternatives ? 'active' : ''} tooltip-trigger`}
            onClick={() => {
              setShowAlternatives(!showAlternatives);
              if (!showAlternatives) setShowKeyboard(false);
            }}
            data-tooltip={t('Show phrase alternatives')}
            aria-label={t('Show phrase alternatives')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
            <span>{t('Alternatives')}</span>
          </button>
        )}
        
        <button 
          className="action-toggle timing-btn tooltip-trigger"
          onClick={handleOpenTiming}
          data-tooltip={t('Edit timing in Studio view')}
          aria-label={t('Edit timing in Studio view')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{t('Timing')}</span>
        </button>
      </div>

      {/* Phrase Alternatives Panel */}
      {showAlternatives && phraseInfo?.hasAlternatives && !isNonArabic && (
        <div className="alternatives-panel">
          {/* Alternative Options - only show non-active, non-original, filtered types */}
          {phraseInfo.alternatives
            .filter(alt => {
              // Skip currently active
              if (phrase.activeAlternativeId === alt.id) return false;
              // Skip certain types by label
              const lowerLabel = (alt.label || '').toLowerCase();
              if (lowerLabel.includes('original')) return false;
              if (lowerLabel.includes('foreign')) return false;
              if (lowerLabel.includes('loanword')) return false;
              return true;
            })
            .map((alt) => {
              const altText = alt.words.map(w => isArabizi ? (w.arabizi || w.text) : w.text).join(' ');
              
              return (
                <button
                  key={alt.id}
                  className="alternative-option"
                  onClick={() => handleSelectAlternative(alt.id)}
                >
                  <span 
                    className="alternative-text" 
                    dir={isArabizi ? 'ltr' : 'rtl'}
                    style={{ fontFamily: fontFamily || 'Noto Naskh Arabic' }}
                  >
                    {altText}
                  </span>
                </button>
              );
            })}
        </div>
      )}
      
      {showKeyboard && !showReplaceAllModal && (
        <div className="keyboard-wrapper">
          <ArabicKeyboard
            value={text}
            onChange={setText}
            onClose={handleSave}
          />
        </div>
      )}

      {/* Replace All Modal */}
      {showReplaceAllModal && (
        <div className="replace-all-modal">
          <div className="replace-all-header">
            <div className="replace-all-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </div>
            <div className="replace-all-title">{t('Multiple Occurrences')}</div>
          </div>
          
          <div className="replace-all-content">
            <div className="replace-all-preview">
              <div className="replace-from">
                <span className="replace-label">{t('From')}</span>
                <span className="replace-text" dir={isArabizi ? 'ltr' : 'rtl'}>{originalTextBeforeEdit}</span>
              </div>
              <div className="replace-arrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div className="replace-to">
                <span className="replace-label">{t('To')}</span>
                <span className="replace-text highlight" dir={isArabizi ? 'ltr' : 'rtl'}>{replaceAllInfo.newText}</span>
              </div>
            </div>
            
            <p className="replace-all-message">
              {rich(t('Found <b>{count} times</b>', { count: replaceAllInfo.count }), { b: (c) => <strong>{c}</strong> })}
            </p>
          </div>
          
          <div className="replace-all-actions">
            <button className="replace-btn replace-all" onClick={handleReplaceAll}>
              {t('Replace All ({count})', { count: replaceAllInfo.count })}
            </button>
            <button className="replace-btn replace-one" onClick={handleReplaceOnlyThis}>
              {t('Only This')}
            </button>
            <button className="replace-btn replace-cancel" onClick={handleCancelReplaceAll}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default InlineWordEditor;
